// SendReportModal — 3-step preview/send flow for client reports.
// Step 0 picks channel + recipients, step 1 previews the rendered report,
// step 2 confirms delivery. FE-only for now (no backend send endpoint yet).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  Tab,
  TextArea,
  TextInput,
} from "./ModalShell";
import type { SendReportCtx } from "./FlowsProvider";
import { recordReportShare, type ReportShareChannel } from "@/lib/proagentApi";
import { channelToWire, buildMailtoURL, buildWhatsAppURL } from "@/lib/reportShareLinks";
import { createOrFetchShare, buildShareUrl } from "@/lib/reportShareApi";

const ALL_CHANNELS = ["Email", "WhatsApp", "ProProp link", "PDF attachment"];

// openNativeClient is the honest "send" half. We don't have an email
// or WhatsApp gateway, so the user's own client takes over. The URL
// building (and its injection-safety) lives in @/lib/reportShareLinks
// so it's unit-tested; this wrapper just performs the side effect.
function openNativeClient(channel: ReportShareChannel, recipients: string, subject: string, body: string) {
  if (channel === "email") {
    window.location.href = buildMailtoURL(recipients, subject, body);
    return;
  }
  if (channel === "whatsapp") {
    window.open(buildWhatsAppURL(recipients, body), "_blank", "noopener,noreferrer");
    return;
  }
  // "link" / "pdf" → no native client to open; user copies the link
  // from the modal footer.
}

type Form = {
  channels?: string[];
  recipients?: string;
  subject?: string;
  message?: string;
};

// Build the /client-report#data=... share URL the existing
// SellerPitch flow uses. There's no backend send-email/whatsapp
// transport yet — what we CAN do honestly is generate the public
// link, copy it to the agent's clipboard, and let them paste it
// into the channel of their choice. The page on the other end
// renders whatever data we encoded.
//
// The encoding is base64(URI-encoded JSON), wrapped in
// #data= so the page reads it from window.location.hash.
function buildShareLink(ctx: SendReportCtx | null, form: Form): string {
  const c = ctx?.caseObj;
  const audience = ctx?.audience ?? "seller";
  const propTag = c
    ? c.type === "property"
      ? `${c.shortName ?? c.name ?? "Listing"} · property case`
      : `${c.shortName ?? c.name ?? "Buyer"} · client case`
    : "Case";
  const data = {
    propTag,
    audience,
    title: form.subject || (audience === "client" ? "Your buyer report" : "Your seller report"),
    text: form.message ?? "",
    generatedAt: new Date().toISOString(),
  };
  const json = JSON.stringify(data);
  // Note: btoa needs a Latin-1 string, so we URI-encode-then-decode
  // the JSON to flatten any multibyte chars into the safe range.
  const hash = "#data=" + encodeURIComponent(btoa(unescape(encodeURIComponent(json))));
  return `${window.location.origin}/client-report${hash}`;
}

// resolveShareLink picks the canonical link to record server-side and
// copy to the agent's clipboard. When ShareCard already minted a real
// tokenised report link (ctx.shareLink → /pro/share/{token}, whose page
// fetches the full report payload — metrics, offers, comps, shortlist),
// THAT is the link the recipient must receive. buildShareLink only
// encodes the agent's subject + personal note into a /client-report#data=
// URL that carries NO report data, so fall back to it only for entry
// points that never minted a token (e.g. SellerPitch). (PROP-170)
// `resolved` is a token link minted lazily inside the modal for entry
// points (the case action bar) that open it with a caseObj but no
// pre-minted shareLink — see the mint effect below (PROP-181). Priority:
// an explicit ctx.shareLink (ShareCard) → the lazily-minted token →
// the data-less buildShareLink fallback (no case id, e.g. SellerPitch).
export function resolveShareLink(ctx: SendReportCtx | null, form: Form, resolved?: string | null): string {
  return ctx?.shareLink || resolved || buildShareLink(ctx, form);
}

export function SendReportModal({
  open,
  ctx,
  onClose,
}: {
  open: boolean;
  ctx: SendReportCtx | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Form>({});
  const [sharedLink, setSharedLink] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "ok" | "err">("idle");
  // PROP-14: the modal records the share intent server-side AND opens
  // the agent's native mail / WhatsApp client. saving is the in-flight
  // POST; saveErr surfaces a backend rejection (validation, network)
  // without blocking the copy-link fallback.
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  // PROP-181: case action-bar entry points pass a caseObj but no
  // pre-minted shareLink. null until the mint effect resolves the real
  // tokenised report link (or stays null when there's nothing to mint).
  const [resolvedLink, setResolvedLink] = useState<string | null>(null);

  // PROP-181: when the modal is opened without a pre-minted shareLink
  // (the case action bar's Send buttons), fetch/mint the real tokenised
  // report link from the caseObj so the recorded + copied link is the
  // full report — not the data-less /client-report fallback. ShareCard
  // entries already supply ctx.shareLink and skip this. A mint failure
  // (e.g. no report generated yet) leaves resolvedLink null →
  // resolveShareLink falls back to buildShareLink.
  useEffect(() => {
    setResolvedLink(null);
    if (!open || ctx?.shareLink) return;
    const caseID = ctx?.caseObj?.id;
    if (!caseID) return;
    let cancelled = false;
    createOrFetchShare(caseID)
      .then((s) => {
        if (!cancelled) setResolvedLink(buildShareUrl(s.token));
      })
      .catch(() => {
        /* leave null; resolveShareLink falls back to buildShareLink */
      });
    return () => {
      cancelled = true;
    };
  }, [open, ctx]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setSharedLink(null);
    setCopyState("idle");
    setSaveErr(null);
    setSaving(false);
    const isClient = ctx?.audience === "client";
    const baseMsg =
      ctx?.message ??
      "Hi — sharing this week's update with key data and next steps. Happy to talk through it whenever works for you.";
    // When ShareCard opens the modal with a freshly-minted link, the
    // recipient should see a clickable URL in the body — agents
    // shouldn't have to retype it. Append on a new line so the
    // personal note still reads naturally.
    const message = ctx?.shareLink
      ? `${baseMsg}\n\nView the report: ${ctx.shareLink}`
      : baseMsg;
    setForm({
      channels: ["Email", "WhatsApp"],
      recipients:
        ctx?.recipients ??
        (isClient ? "client@example.com" : "seller@example.com"),
      subject:
        ctx?.subject ??
        (isClient ? "Your shortlist — this week" : `${ctx?.caseObj?.shortName ?? "Listing"} — this week`),
      message,
    });
  }, [open, ctx]);

  const isClient = ctx?.audience === "client";
  const audienceName =
    ctx?.recipientName ?? (isClient ? "your buyer" : "your seller");

  return (
    <ModalShell open={open} onClose={onClose} width={780} label="Send report">
      <ModalHeader
        eyebrow={
          isClient
            ? t("agent.flows.sendReport.eyebrowBuyer", { defaultValue: "Send to buyer" })
            : t("agent.flows.sendReport.eyebrowSeller", { defaultValue: "Send to seller" })
        }
        title={
          step === 2
            ? t("agent.flows.sendReport.sentTitle", { audience: audienceName, defaultValue: `Sent to ${audienceName}` })
            : t("agent.flows.sendReport.sendTitle", {
                role: isClient ? "buyer" : "seller",
                defaultValue: `Send the ${isClient ? "buyer" : "seller"} report`,
              })
        }
        hint={
          step === 0
            ? t("agent.flows.sendReport.hint0", {
                defaultValue: "Confirm channel and recipient. We'll generate a copy that's already drafted.",
              })
            : step === 1
              ? t("agent.flows.sendReport.hint1", { defaultValue: "Review before sending. AI has tuned the tone." })
              : t("agent.flows.sendReport.hint2", { defaultValue: "They've been notified via your selected channels." })
        }
        onClose={onClose}
      />

      {step !== 2 && (
        <div
          style={{
            padding: "0 26px",
            borderTop: "1px solid var(--ag-rule)",
            borderBottom: "1px solid var(--ag-rule)",
            display: "flex",
            gap: 4,
          }}
        >
          <Tab active={step === 0} onClick={() => setStep(0)}>
            {t("agent.flows.sendReport.tabChannel", { defaultValue: "1 · Channel" })}
          </Tab>
          <Tab active={step === 1} onClick={() => setStep(1)}>
            {t("agent.flows.sendReport.tabPreview", { defaultValue: "2 · Preview" })}
          </Tab>
        </div>
      )}

      <ModalBody style={{ display: "block" }}>
        {step === 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field
              label={t("agent.flows.sendReport.channels", { defaultValue: "Channels" })}
              hint={t("agent.flows.sendReport.channelsHint", {
                defaultValue: "The same content, formatted appropriately per channel.",
              })}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {ALL_CHANNELS.map((c) => {
                  const active = form.channels?.includes(c);
                  return (
                    <Chip
                      key={c}
                      active={active}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          channels: active
                            ? (f.channels ?? []).filter((x) => x !== c)
                            : [...(f.channels ?? []), c],
                        }))
                      }
                    >
                      {c}
                    </Chip>
                  );
                })}
              </div>
            </Field>
            <Field
              label={t("agent.flows.sendReport.recipients", { defaultValue: "Recipients" })}
              hint={t("agent.flows.sendReport.recipientsHint", {
                defaultValue: "Comma-separated. We'll match to existing contacts where possible.",
              })}
            >
              <TextInput
                value={form.recipients}
                onChange={(v) => setForm((f) => ({ ...f, recipients: v }))}
              />
            </Field>
            <Field label={t("agent.flows.sendReport.subject", { defaultValue: "Subject" })}>
              <TextInput
                value={form.subject}
                onChange={(v) => setForm((f) => ({ ...f, subject: v }))}
              />
            </Field>
            <Field
              label={t("agent.flows.sendReport.note", { defaultValue: "Personal note" })}
              hint={t("agent.flows.sendReport.noteHint", {
                defaultValue: "Sits above the report. Keep it short — the data does the talking.",
              })}
            >
              <TextArea
                value={form.message}
                onChange={(v) => setForm((f) => ({ ...f, message: v }))}
                rows={3}
              />
            </Field>
            {/* PROP-189: honest delivery note. The old block here was a
                pre-checked "auto-send every Monday" toggle (no scheduler
                exists) above an AIHint claiming a fabricated "read within
                4h on average" stat (no read-receipt data source). This
                flow only prepares a link + opens the agent's own client —
                say exactly that, promise nothing the product can't do. */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "10px 12px",
                background: "var(--ag-surface)",
                borderRadius: 8,
                border: "1px solid var(--ag-rule)",
                fontSize: 12.5,
                color: "var(--ag-ink-3)",
                lineHeight: 1.5,
              }}
            >
              {t("agent.flows.sendReport.deliveryNote", {
                defaultValue:
                  "ProProp prepares the link and opens your own email or WhatsApp to send — it doesn't deliver the message or track opens on your behalf.",
              })}
            </div>
          </div>
        )}

        {step === 1 && <Preview ctx={ctx} form={form} isClient={isClient} />}

        {step === 2 && <SuccessState audienceName={audienceName} channels={form.channels} />}
      </ModalBody>

      {step !== 2 ? (
        <ModalFooter
          leftHint={
            step === 0
              ? `${t("agent.flows.sendReport.selected", { defaultValue: "Selected" })}: ${(form.channels ?? []).join(" · ") || "none"}`
              : t("agent.flows.sendReport.tone", { defaultValue: "AI tone: balanced · Length: ~1 min read" })
          }
        >
          {step === 1 && (
            <Btn ghost onClick={() => setStep(0)}>
              {t("agent.flows.back", { defaultValue: "Back" })}
            </Btn>
          )}
          {step === 0 && (
            <Btn ghost onClick={onClose}>
              {t("agent.flows.cancel", { defaultValue: "Cancel" })}
            </Btn>
          )}
          {step === 0 && (
            <Btn
              primary
              disabled={!(form.channels ?? []).length}
              onClick={() => setStep(1)}
            >
              {t("agent.flows.sendReport.preview", { defaultValue: "Preview" })}
            </Btn>
          )}
          {step === 1 && (
            <Btn
              primary
              disabled={saving}
              onClick={async () => {
                // PROP-14: build the link, record the share intent
                // server-side, copy to clipboard, then open the
                // agent's native client for the first eligible
                // channel. Errors on the POST don't block the rest —
                // the agent still gets the link to paste manually.
                const url = resolveShareLink(ctx, form, resolvedLink);
                setSharedLink(url);
                setSaving(true);
                setSaveErr(null);

                // 1) Persist the audit row (best-effort; surface
                // failure but never block the rest of the flow).
                const caseID = ctx?.caseObj?.id;
                const wireChannels = (form.channels ?? []).map(channelToWire);
                if (caseID) {
                  try {
                    await recordReportShare(caseID, {
                      audience: isClient ? "buyer" : "seller",
                      channels: wireChannels,
                      recipient: form.recipients ?? "",
                      share_link: url,
                      subject: form.subject ?? "",
                      message: form.message ?? "",
                    });
                  } catch (e) {
                    setSaveErr(
                      e instanceof Error
                        ? e.message
                        : "Couldn't save the share record — link is still usable",
                    );
                  }
                }

                // 2) Clipboard copy of the link for paste-friendliness.
                try {
                  await navigator.clipboard?.writeText(url);
                  setCopyState("ok");
                } catch {
                  setCopyState("err");
                }

                // 3) Open the native client. We pick the first
                // actionable channel — email > whatsapp. Link / PDF
                // have no deep-link; user copies from the footer.
                const native = wireChannels.find((c) => c === "email") ??
                  wireChannels.find((c) => c === "whatsapp");
                if (native) {
                  openNativeClient(
                    native,
                    form.recipients ?? "",
                    form.subject ?? "",
                    form.message ?? "",
                  );
                }

                setSaving(false);
                setStep(2);
              }}
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7l4 4 6-8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {saving
                ? t("agent.flows.sendReport.sending", { defaultValue: "Recording…" })
                : t("agent.flows.sendReport.sendNow", { defaultValue: "Send now" })}
            </Btn>
          )}
        </ModalFooter>
      ) : (
        <ModalFooter
          leftHint={
            // saveErr takes precedence: the agent needs to know the
            // audit row didn't land before they think the share was
            // recorded. The link itself is still usable.
            saveErr
              ? t("agent.flows.sendReport.saveErr", {
                  defaultValue: "Couldn't save the share record: {{err}}. Link is still usable.",
                  err: saveErr,
                })
              : sharedLink
              ? copyState === "ok"
                ? t("agent.flows.sendReport.copied", {
                    defaultValue: "Share recorded · link copied — paste into WhatsApp/email if needed.",
                  })
                : t("agent.flows.sendReport.copyHint", {
                    defaultValue: "Share recorded — long-press the link to copy.",
                  })
              : t("agent.flows.sendReport.resend", {
                  defaultValue: "You can re-send anytime from the Report tab.",
                })
          }
        >
          {sharedLink && (
            <Btn
              ghost
              onClick={async () => {
                try {
                  await navigator.clipboard?.writeText(sharedLink);
                  setCopyState("ok");
                } catch {
                  setCopyState("err");
                }
              }}
            >
              {t("agent.flows.sendReport.copyLink", { defaultValue: "Copy link" })}
            </Btn>
          )}
          <Btn primary onClick={onClose}>
            {t("agent.flows.sendReport.done", { defaultValue: "Done" })}
          </Btn>
        </ModalFooter>
      )}
    </ModalShell>
  );
}

function Preview({
  ctx,
  form,
  isClient,
}: {
  ctx: SendReportCtx | null;
  form: Form;
  isClient: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 220px",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div
        style={{
          background: "var(--ag-surface)",
          border: "1px solid var(--ag-rule)",
          borderRadius: 12,
          padding: 22,
          fontFamily: "var(--ag-font-serif)",
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "var(--ag-accent)",
            fontFamily: "var(--ag-font-ui)",
            marginBottom: 8,
          }}
        >
          {isClient ? "Buyer update" : "Seller update"} · This week
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1.2,
            marginBottom: 14,
            letterSpacing: -0.2,
          }}
        >
          {form.subject || (isClient ? "Your shortlist this week" : "This week's update")}
        </div>
        <div
          style={{
            fontSize: 13.5,
            color: "var(--ag-ink-2)",
            lineHeight: 1.6,
            fontFamily: "var(--ag-font-ui)",
            whiteSpace: "pre-line",
            marginBottom: 14,
          }}
        >
          {form.message}
        </div>
        <div style={{ height: 1, background: "var(--ag-rule)", margin: "12px 0 16px" }} />
        <div
          style={{
            fontSize: 13.5,
            color: "var(--ag-ink-2)",
            lineHeight: 1.6,
            fontFamily: "var(--ag-font-ui)",
          }}
        >
          {ctx?.caseObj?.aiNote ||
            "Activity summary, themes from buyer feedback, and a recommended next step are auto-generated and inserted here when you send."}
        </div>
        <div style={{ height: 1, background: "var(--ag-rule)", margin: "14px 0 10px" }} />
        <div
          style={{ fontSize: 11.5, color: "var(--ag-ink-3)", fontFamily: "var(--ag-font-ui)" }}
        >
          Generated by ProProp
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div
          style={{
            padding: 12,
            background: "var(--ag-surface-2)",
            borderRadius: 10,
            border: "1px solid var(--ag-rule)",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--ag-ink-3)",
              marginBottom: 8,
            }}
          >
            Sending to
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--ag-ink)",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {form.recipients}
          </div>
        </div>
        <div
          style={{
            padding: 12,
            background: "var(--ag-surface-2)",
            borderRadius: 10,
            border: "1px solid var(--ag-rule)",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "var(--ag-ink-3)",
              marginBottom: 8,
            }}
          >
            Channels
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {(form.channels ?? []).map((c) => (
              <span
                key={c}
                style={{
                  fontSize: 11,
                  padding: "3px 8px",
                  borderRadius: 999,
                  background: "var(--ag-accent-soft)",
                  color: "var(--ag-accent)",
                  fontWeight: 600,
                }}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessState({
  audienceName,
  channels,
}: {
  audienceName: string;
  channels?: string[];
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        padding: "20px 0 8px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "var(--ag-accent-soft)",
          color: "var(--ag-accent)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12l5 5L20 7"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div
        style={{
          fontFamily: "var(--ag-font-serif)",
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: -0.2,
        }}
      >
        {t("agent.flows.sendReport.successTitle", {
          audience: audienceName,
          defaultValue: `Report ready to send to ${audienceName}`,
        })}
      </div>
      <div style={{ fontSize: 13, color: "var(--ag-ink-3)", maxWidth: 460, lineHeight: 1.55 }}>
        {t("agent.flows.sendReport.successBody", {
          defaultValue: `Link copied and your ${(channels || []).join(
            " + ",
          )} client is open. ProProp doesn't track opens — follow up with ${audienceName} directly.`,
          channels: (channels || []).join(" + "),
          audience: audienceName,
        })}
      </div>
    </div>
  );
}
